import { useCallback } from "react";
import AppleSignin from "react-apple-signin-auth";
import { api } from "@/utils/api";
import { storeTokenPair } from "@/utils/tokenRefresh";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";
import { toast } from "sonner";

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_SERVICES_ID;
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI;
const APPLE_ENABLED = Boolean(APPLE_CLIENT_ID && APPLE_REDIRECT_URI);

// Module-scope nonce — generated once at first import (anti-replay).
const SESSION_NONCE = (() => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `apple-${Date.now()}`;
})();

/**
 * Small hook that returns true when the <html> element carries the
 * `dark` class. Re-exported from @/hooks/useIsDarkMode so other
 * components (e.g. LoginPage's Google button) can stay in sync.
 */

/**
 * Apple Sign-In button — rendered with a CUSTOM button so its size,
 * label and theming align 1:1 with the Google button next to it.
 *
 * - 320px width, 44px height (matches GoogleLogin width="320")
 * - Label: "Sign in with Apple" (consistent with "Sign in with Google")
 * - Theme: light mode -> white bg + black text, dark mode -> black bg
 *   + white text (mirrors GoogleLogin's outline vs filled_black themes).
 * - Uses react-apple-signin-auth's `render` prop so we keep the popup
 *   plumbing while replacing the visual chrome.
 */
export const AppleSignInButton = () => {
  const isDark = useIsDarkMode();

  const handleSuccess = useCallback(async (response) => {
    try {
      const idToken = response?.authorization?.id_token;
      if (!idToken) {
        toast.error("Apple sign-in returned no token — please try again.");
        return;
      }

      const fullName = response?.user?.name;
      const displayName = fullName
        ? [fullName.firstName, fullName.lastName].filter(Boolean).join(" ").trim()
        : undefined;

      const { data } = await api.post("/auth/apple/web", {
        id_token: idToken,
        user_name: displayName || undefined,
      });

      const { user, session_token, access_token, refresh_token } = data;
      storeTokenPair({ access_token, refresh_token, session_token });
      localStorage.setItem("user", JSON.stringify(user));

      toast.success(`Welcome${user.name ? `, ${user.name}` : ""}!`);
      window.location.href = "/";
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        toast.error(detail || "This Apple ID is not authorized to access Travel Gadjet yet.");
      } else {
        toast.error(detail || "Apple sign-in failed. Please try again.");
      }
    }
  }, []);

  const handleError = useCallback((err) => {
    if (err?.error === "popup_closed_by_user") return;
    toast.error("Apple sign-in was interrupted. Please try again.");
  }, []);

  if (!APPLE_ENABLED) return null;

  // Custom button styling — pin width to 320px (matches the Google
  // button width) and switch colour scheme on theme.
  const buttonClass = [
    "flex items-center justify-center gap-3",
    "w-[320px] h-[44px] rounded-md",
    "font-medium text-sm",
    "border transition-colors",
    isDark
      ? "bg-black text-white border-black hover:bg-gray-900"
      : "bg-white text-gray-900 border-gray-300 hover:bg-gray-50",
  ].join(" ");

  return (
    <div className="flex justify-center" data-testid="apple-signin-wrapper">
      <AppleSignin
        authOptions={{
          clientId: APPLE_CLIENT_ID,
          scope: "email name",
          redirectURI: APPLE_REDIRECT_URI,
          state: "origin:web",
          nonce: SESSION_NONCE,
          usePopup: true,
        }}
        onSuccess={handleSuccess}
        onError={handleError}
        // Render a custom button so the visuals match the Google button.
        render={(renderProps) => (
          <button
            type="button"
            onClick={renderProps.onClick}
            disabled={!renderProps}
            className={buttonClass}
            data-testid="apple-signin-button"
            aria-label="Sign in with Apple"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 384 512"
              className="w-4 h-4"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
            </svg>
            <span>Sign in with Apple</span>
          </button>
        )}
      />
    </div>
  );
};

export default AppleSignInButton;
